import {
	EventDuration,
	EventSession,
	EventStatusV2,
	EventV2Response,
} from "@onair/repositories";
import { EventType } from "@onair/types/enums";
import { FindManyFilter } from "@onair/types/extends";
import { EventPricingFilter, EventStateFilter } from "@onair/ui/event/types";
import dayjs from "@onair/utils/dayjs";

export enum BusinessType {
	CSMO = "csmo",
	MCG = "mcg",
}

export const BUSINESS_SPECS = {
	[BusinessType.CSMO]: {
		value: BusinessType.CSMO,
		name: "CSMO",
		where: (query: FindManyFilter<Event>) => {
			return {
				...query.where,
				businesses: {
					some: {
						business: {
							slug: BusinessType.CSMO,
						},
					},
				},
			};
		},
	},
	[BusinessType.MCG]: {
		value: BusinessType.MCG,
		name: "MCG",
		where: (query: FindManyFilter<Event>) => {
			return {
				...query.where,
				businesses: {
					some: {
						business: {
							slug: BusinessType.MCG,
						},
					},
				},
			};
		},
	},
};

export const EVENT_TYPE_SPECS = {
	[EventType.TWO_WAY]: {
		colorName: "secondary",
		color: "#9723F9",
		bgColor: "rgba(151, 35, 249, 0.08)",
		borderColor: "rgba(151, 35, 249, 0.12)",
		colorHighlight: "rgba(151, 35, 249, 0.48)",
		value: EventType.TWO_WAY,
		name: "Tương tác hai chiều",
	},
	[EventType.ONE_TO_ONE]: {
		colorName: "primary",
		color: "#0050FF",
		bgColor: "rgba(0, 80, 255, 0.08)",
		borderColor: "rgba(0, 80, 255, 0.16)",
		colorHighlight: "rgba(0, 80, 255, 0.48)",
		value: EventType.ONE_TO_ONE,
		name: "Trò chuyện 1:1",
	},
	[EventType.ONE_WAY]: {
		colorName: "warning",
		color: "#FFAB00",
		bgColor: "rgba(255, 171, 0, 0.12)",
		borderColor: "rgba(255, 245, 204, 1)",
		colorHighlight: "rgba(255, 171, 0, 0.48)",
		value: EventType.ONE_WAY,
		name: "Phát sóng một chiều",
	},
};

export const EVENT_COLORS = {
	colorName: "primary",
	color: "#0050FF",
	bgLightColor: "rgba(0, 80, 255, 0.12)",
	bgLabelColor: "rgba(0, 80, 255, 0.08)",
	bgColor: "rgba(0, 80, 255, 0.2)",
	borderColor: "rgba(0, 80, 255, 0.16)",
	colorHighlight: "rgba(0, 80, 255, 0.48)",
};

export const EVENT_STATE_SPECS = {
	[EventStateFilter.ALL]: {
		value: EventStateFilter.ALL,
		name: "Tất cả",
		where: (query: FindManyFilter<Event>) => {
			return {
				...query.where,
				NOT: {
					endDate: {
						lte: dayjs().utc(), // <--- loại những event đã qua(PAST)
					},
				},
			};
		},
	},
	[EventStateFilter.LIVE]: {
		value: EventStateFilter.LIVE,
		name: "Đang diễn ra",
		where: (query: FindManyFilter<Event>) => {
			return {
				...query.where,
				startDate: {
					lte: dayjs().utc(),
				},
				endDate: {
					gt: dayjs().utc(),
				},
			};
		},
	},
	[EventStateFilter.TODAY]: {
		value: EventStateFilter.TODAY,
		name: "Diễn ra hôm nay",
		where: (query: FindManyFilter<Event>) => {
			return {
				...query.where,
				startDate: {
					lte: dayjs().endOf("day").utc(),
					gt: dayjs().utc(),
				},
			};
		},
	},
	[EventStateFilter.FUTURE]: {
		value: EventStateFilter.FUTURE,
		name: "Sắp diễn ra",
		where: (query: FindManyFilter<Event>) => {
			return {
				...query.where,
				startDate: {
					gt: dayjs().utc(),
				},
			};
		},
	},
	[EventStateFilter.PAST]: {
		value: EventStateFilter.PAST,
		name: "Đã diễn ra",
		where: (query: FindManyFilter<Event>) => {
			return {
				...query.where,
				endDate: {
					lte: dayjs().utc(),
				},
			};
		},
	},
};

export const EVENT_PRICING_SPECS = {
	[EventPricingFilter.ALL]: {
		value: EventPricingFilter.ALL,
		name: "Tất cả",
		orderBy: (query: FindManyFilter<Event>) => {
			return {};
		},
	},
	[EventPricingFilter.LOWEST_TO_HIGHEST]: {
		value: EventPricingFilter.LOWEST_TO_HIGHEST,
		name: "Thấp -> Cao",
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		orderBy: (query: FindManyFilter<Event>) => {
			return {
				farePrice: "asc", // 'asc' for ascending order
			};
		},
	},
	[EventPricingFilter.HIGHEST_TO_LOWEST]: {
		value: EventPricingFilter.HIGHEST_TO_LOWEST,
		name: "Cao -> Thấp",
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		orderBy: (query: FindManyFilter<Event>) => {
			return {
				farePrice: "desc",
			};
		},
	},
};

export const EVENT_DURATION_SPECS = {
	[EventDuration.Value]: {
		label: "Tất cả",
	},
	[EventDuration.FROM0TO30MINUTES]: {
		label: "0 - 30 phút",
	},
	[EventDuration.FROM30TO60MINUTES]: {
		label: "30 - 60 phút",
	},
	[EventDuration.FROM60TO90MINUTES]: {
		label: "60 - 90 phút",
	},
	[EventDuration.FROM90TO120MINUTES]: {
		label: "90 - 120 phút",
	},
	[EventDuration.OVER120MINUTES]: {
		label: "Trên 120 phút",
	},
	[EventDuration.DURATION_IN_DAY]: {
		label: "Kéo dài trong 1 ngày",
	},
	[EventDuration.DURATION_OVER_DAY]: {
		label: "Kéo dài trong nhiều ngày",
	},
};

export enum EventStates {
	DRAFT = "DRAFT",
	TODAY = "TODAY",
	PAST = "PAST",
	CURRENT = "CURRENT",
	FUTURE = "FUTURE",
}

export const getEventState = (
	event:
		| Pick<EventV2Response, "startDate" | "endDate" | "status">
		| Pick<EventSession, "startDate" | "endDate">,
	current = dayjs(),
	isHost?: boolean,
): EventStates | undefined => {
	if (!event) {
		return undefined;
	}

	if ((event as EventV2Response)?.status === EventStatusV2.DRAFT) {
		return EventStates.DRAFT;
	}
	if (dayjs(event.endDate).isBefore(current)) {
		return EventStates.PAST;
	}
	if (current.isAfter(dayjs(event.startDate)) && isHost) {
		return EventStates.CURRENT;
	}
	if (
		dayjs(event.startDate).isBefore(current.endOf("day")) &&
		dayjs(event.startDate).isAfter(current)
	) {
		return EventStates.TODAY;
	}

	if (
		dayjs(event.endDate).isAfter(current) &&
		dayjs(event.startDate).isBefore(current)
	) {
		return EventStates.CURRENT;
	}
	return EventStates.FUTURE;
};

export const ZALO_LINK_REGEX = /^https:\/\/zalo\.me\/g\/[a-zA-Z0-9]+$/;
export const ZALO_LINK_MESSAGE =
	"Link phải có định dạng https://zalo.me/g/<GroupID>";
